import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Paper, Typography } from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import CommonInput from '../../component/form/common-input.component';
import i18n from '../../i18n';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import MainPageState from '../main/main-page-states';
import { MenuLibraryManagementState } from './menu-library-management-state';
import { MenuLanguageMenuButton } from './menu-library-language-menu-button.component';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import './scss/menu-library-create-menu-dialog.style.scss';
import './scss/menu-library-add-subcategory-dialog.style.scss';

const OUTER_MARGIN_PX = 16;

type Props = {
    state: MenuLibraryManagementState;
};

/**
 * 菜单库详情 — 新增 / 编辑子类别（同一弹窗，`detailAddSubcategoryDialogMode` 区分）
 */
export const MenuLibraryAddSubcategoryDialog: React.FC<Props> = observer(({ state }) => {
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const sidebarOffset = useMemo(() => {
        const { open, isMobileDevice, sidebarWidth } = mainPageData;
        return open ? sidebarWidth.expand : isMobileDevice ? sidebarWidth.hidden : sidebarWidth.reduced;
    }, [mainPageData.open, mainPageData.isMobileDevice, mainPageData.sidebarWidth]);

    const submitting = state.isDetailAddSubcategorySubmitting;
    const isEdit = state.detailAddSubcategoryDialogMode === 'edit';

    return (
        <DialogComponent
            className="menu-library-add-subcategory-dialog-root"
            open={state.isDetailAddSubcategoryDialogOpen}
            fullWidth
            maxWidth={false}
            onClose={() => state.closeDetailAddSubcategoryDialog()}
            PaperProps={{
                sx: {
                    margin: `${OUTER_MARGIN_PX}px auto`,
                    width: '100%',
                    maxWidth: 'min(704px, calc(100vw - 32px))',
                    maxHeight: `calc(100vh - ${2 * OUTER_MARGIN_PX}px)`,
                    borderRadius: '16px',
                    border: '2px solid var(--fill-primary-300)',
                    background: 'var(--fill-primary-200)',
                    boxShadow: 'none',
                },
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--overlay-800-a25)',
                },
                '& .MuiBackdrop-root': {
                    left: sidebarOffset,
                    width: `calc(100% - ${sidebarOffset})`,
                },
                '& .MuiDialog-container > .MuiPaper-root': {
                    borderRadius: '16px',
                    border: '2px solid var(--fill-primary-300)',
                    background: 'var(--fill-primary-200)',
                    boxShadow: 'none',
                },
                '& .MuiDialogContent-root': {
                    overflow: 'visible',
                },
                '& .MuiDialogTitle-root': {
                    padding: '24px 24px 0',
                },
            }}
            defaultHeaderProps={{
                title: isEdit
                    ? i18n.t('menu_library_detail_edit_subcategory')
                    : i18n.t('menu_library_create_dialog_add_subcategory'),
                onClose: () => state.closeDetailAddSubcategoryDialog(),
            }}
            footer={
                <Box className="menu-library-add-subcategory-dialog__footer">
                    <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        disableElevation
                        className="menu-library-add-subcategory-dialog__btn-cancel"
                        disabled={submitting}
                        onClick={() => state.closeDetailAddSubcategoryDialog()}
                    >
                        {i18n.t('menu_library_detail_add_subcategory_dialog_cancel')}
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        className="menu-library-add-subcategory-dialog__btn-save"
                        disabled={submitting}
                        startIcon={<SaveIcon className="menu-library-add-subcategory-dialog__save-icon" aria-hidden />}
                        onClick={() => void state.submitDetailAddSubcategoryDialog()}
                    >
                        {i18n.t('menu_library_detail_save_changes')}
                    </Button>
                </Box>
            }
        >
            <Box className="menu-library-add-subcategory-dialog__body">
                {/* 1. 子类别菜单名称（通栏） */}
                <Paper elevation={0} className="menu-library-add-subcategory-dialog__inner-card menu-library-add-subcategory-dialog__inner-card--name">
                    <Typography component="div" className="menu-library-add-subcategory-dialog__card-label--secondary">
                        {i18n.t('menu_library_create_dialog_subcategory_col_name')}
                    </Typography>
                    <CommonInput
                        label=""
                        inputType="INPUT"
                        variant="outlined"
                        fullWidth
                        size="small"
                        className="menu-library-add-subcategory-dialog__name-input"
                        placeholder={i18n.t('menu_library_create_dialog_subcategory_name_placeholder')}
                        value={state.detailAddSubcategoryNameDraft}
                        disabled={submitting}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            state.setDetailAddSubcategoryNameInput(e.currentTarget.value);
                        }}
                    />
                </Paper>

                {/* 2. 菜品语言显示（通栏，左右分布） */}
                <Paper elevation={0} className="menu-library-add-subcategory-dialog__inner-card menu-library-add-subcategory-dialog__inner-card--lang">
                    <Box className="menu-library-add-subcategory-dialog__lang-row">
                        <Typography component="div" className="menu-library-add-subcategory-dialog__card-label--inline">
                            {i18n.t('menu_library_add_dish_dish_lang_display')}
                        </Typography>
                        {/*
                         * `.menu-library-create-dialog` 祖先：子类语言触发器样式（与新建弹窗一致）
                         */}
                        <Box className="menu-library-create-dialog menu-library-add-subcategory-dialog__lang-control">
                            <MenuLanguageMenuButton
                                state={state}
                                value={state.detailAddSubcategoryDisplayLang}
                                onChange={(lang) => state.setDetailAddSubcategoryDisplayLang(lang)}
                                variant="subcategoryRow"
                            />
                        </Box>
                    </Box>
                </Paper>

                {/* 3. 每人 / 桌台（两列） */}
                <Box className="menu-library-add-subcategory-dialog__limits-row">
                    <Paper elevation={0} className="menu-library-add-subcategory-dialog__inner-card menu-library-add-subcategory-dialog__inner-card--limit">
                        <Typography component="div" className="menu-library-add-subcategory-dialog__limit-label">
                            {i18n.t('menu_library_create_dialog_subcategory_col_per_person')}
                        </Typography>
                        <CommonInput
                            label=""
                            inputType="INPUT"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className="menu-library-add-subcategory-dialog__limit-input"
                            placeholder={i18n.t('menu_library_create_dialog_placeholder_none')}
                            value={state.detailAddSubcategoryPerPersonLimit}
                            disabled={submitting}
                            inputProps={{
                                inputMode: 'numeric' as const,
                                pattern: '[0-9]*',
                                'aria-valuemin': 0,
                                'aria-valuemax': 10000,
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                state.setDetailAddSubcategoryPerPersonLimit(e.currentTarget.value);
                            }}
                        />
                    </Paper>
                    <Paper elevation={0} className="menu-library-add-subcategory-dialog__inner-card menu-library-add-subcategory-dialog__inner-card--limit">
                        <Typography component="div" className="menu-library-add-subcategory-dialog__limit-label">
                            {i18n.t('menu_library_create_dialog_subcategory_col_per_table')}
                        </Typography>
                        <CommonInput
                            label=""
                            inputType="INPUT"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className="menu-library-add-subcategory-dialog__limit-input"
                            placeholder={i18n.t('menu_library_create_dialog_placeholder_none')}
                            value={state.detailAddSubcategoryPerTableLimit}
                            disabled={submitting}
                            inputProps={{
                                inputMode: 'numeric' as const,
                                pattern: '[0-9]*',
                                'aria-valuemin': 0,
                                'aria-valuemax': 10000,
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                state.setDetailAddSubcategoryPerTableLimit(e.currentTarget.value);
                            }}
                        />
                    </Paper>
                </Box>
            </Box>
        </DialogComponent>
    );
});
